import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { BellRing, Shield, Eye, Lock, Save, Loader2 } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import api from '../../api/axios';
import styles from './Settings.module.css';

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [preferences, setPreferences] = useState({
    visitorAlerts: true,
    societyNotices: true,
    billReminders: true,
    profileVisibility: 'society',
    showContactNumber: false,
  });

  const [passwordForm, setPasswordForm] = useState({
    current: '',
    newPass: '',
    confirm: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const { data } = await api.get('/users/profile');
      if (data.settings) {
        setPreferences({
          visitorAlerts: data.settings.notifications?.visitorAlerts ?? true,
          societyNotices: data.settings.notifications?.societyNotices ?? true,
          billReminders: data.settings.notifications?.billReminders ?? true,
          profileVisibility: data.settings.privacy?.profileVisibility || 'society',
          showContactNumber: data.settings.privacy?.showContactNumber ?? false,
        });
      }
      setLoading(false);
    } catch (err) {
      toast.error('Failed to load settings');
      setLoading(false);
    }
  };

  const handlePrefChange = (e) => {
    const { name, type, checked, value } = e.target;
    setPreferences({
      ...preferences,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const savePreferences = async () => {
    try {
      const payload = {
        settings: {
          notifications: {
            visitorAlerts: preferences.visitorAlerts,
            societyNotices: preferences.societyNotices,
            billReminders: preferences.billReminders,
          },
          privacy: {
            profileVisibility: preferences.profileVisibility,
            showContactNumber: preferences.showContactNumber,
          }
        }
      };
      await api.put('/users/profile', payload);
      toast.success('Preferences saved successfully!');
    } catch (err) {
      toast.error('Failed to save preferences');
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if(passwordForm.newPass !== passwordForm.confirm) {
      toast.error("Passwords do not match!");
      return;
    }
    
    try {
      await api.put('/users/profile', { password: passwordForm.newPass });
      toast.success("Password changed successfully!");
      setPasswordForm({ current: '', newPass: '', confirm: '' });
    } catch (err) {
      toast.error("Failed to change password. Please try again.");
    }
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}><Loader2 className="spinner" /></div>;

  return (
    <div className={styles.container}>
      <PageHeader 
        title="Settings" 
        breadcrumb={['Account', 'Settings']} 
      />

      <div className={styles.contentGrid}>
        {/* Left Column: Preferences */}
        <div className={styles.column}>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <BellRing size={20} className={styles.icon} />
              <h2>Notification Preferences</h2>
            </div>
            <div className={styles.cardBody}>
              <label className={styles.toggleRow}>
                <div>
                  <span className={styles.labelTitle}>Visitor Alerts</span>
                  <span className={styles.labelDesc}>Get notified when visitors arrive at the gate</span>
                </div>
                <div className={styles.toggle}>
                  <input type="checkbox" name="visitorAlerts" checked={preferences.visitorAlerts} onChange={handlePrefChange} />
                  <span className={styles.slider}></span>
                </div>
              </label>

              <label className={styles.toggleRow}>
                <div>
                  <span className={styles.labelTitle}>Society Notices</span>
                  <span className={styles.labelDesc}>Updates and circulars from management</span>
                </div>
                <div className={styles.toggle}>
                  <input type="checkbox" name="societyNotices" checked={preferences.societyNotices} onChange={handlePrefChange} />
                  <span className={styles.slider}></span>
                </div>
              </label>

              <label className={styles.toggleRow}>
                <div>
                  <span className={styles.labelTitle}>Bill Reminders</span>
                  <span className={styles.labelDesc}>Alerts for upcoming maintenance dues</span>
                </div>
                <div className={styles.toggle}>
                  <input type="checkbox" name="billReminders" checked={preferences.billReminders} onChange={handlePrefChange} />
                  <span className={styles.slider}></span>
                </div>
              </label>
            </div>
          </div>

          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <Eye size={20} className={styles.icon} />
              <h2>Privacy Settings</h2>
            </div>
            <div className={styles.cardBody}>
              <div className={styles.inputGroup}>
                <label>Profile Visibility in Directory</label>
                <select name="profileVisibility" value={preferences.profileVisibility} onChange={handlePrefChange}>
                  <option value="public">Public (All Users)</option>
                  <option value="society">Society Members Only</option>
                  <option value="hidden">Hidden</option>
                </select>
              </div>
              <label className={styles.toggleRow} style={{ marginTop: '1rem' }}>
                <div>
                  <span className={styles.labelTitle}>Show Contact Number</span>
                  <span className={styles.labelDesc}>Allow neighbours to see your phone number</span>
                </div>
                <div className={styles.toggle}>
                  <input type="checkbox" name="showContactNumber" checked={preferences.showContactNumber} onChange={handlePrefChange} />
                  <span className={styles.slider}></span>
                </div>
              </label>
            </div>
          </div>
          
          <button className={styles.saveBtn} onClick={savePreferences} style={{ width: '100%' }}>
            <Save size={16} /> Save Preferences
          </button>
        </div>

        {/* Right Column: Security */}
        <div className={styles.column}>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <Shield size={20} className={styles.icon} />
              <h2>Account Security</h2>
            </div>
            <div className={styles.cardBody}>
              <form onSubmit={handlePasswordSubmit} className={styles.passwordForm}>
                <div className={styles.inputGroup}>
                  <label>Current Password</label>
                  <input 
                    type="password" 
                    value={passwordForm.current} 
                    onChange={e => setPasswordForm({...passwordForm, current: e.target.value})} 
                    required 
                  />
                </div>
                <div className={styles.inputGroup}>
                  <label>New Password</label>
                  <input 
                    type="password" 
                    value={passwordForm.newPass} 
                    onChange={e => setPasswordForm({...passwordForm, newPass: e.target.value})} 
                    required 
                  />
                </div>
                <div className={styles.inputGroup}>
                  <label>Confirm New Password</label>
                  <input 
                    type="password" 
                    value={passwordForm.confirm} 
                    onChange={e => setPasswordForm({...passwordForm, confirm: e.target.value})} 
                    required 
                  />
                </div>
                <button type="submit" className={styles.saveBtn}>
                  <Save size={16} /> Update Password
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
